import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { toast } from "sonner";
import { downloadCsv } from "../lib/download";
import { Trophy, RefreshCw, Loader2, Download, Award } from "lucide-react";
import logger from "../lib/logger";

const WEEK_OPTIONS = [4, 8, 12];

function scoreColor(score) {
  if (score == null) return { bg: "#F3F4F6", fg: "#6B7280", border: "#E5E7EB" };
  if (score >= 85) return { bg: "#16a34a", fg: "#FFFFFF", border: "#15803d" };
  if (score >= 65) return { bg: "#65a30d", fg: "#FFFFFF", border: "#4d7c0f" };
  if (score >= 40) return { bg: "#f59e0b", fg: "#1f2937", border: "#d97706" };
  return { bg: "#ef4444", fg: "#FFFFFF", border: "#b91c1c" };
}

export default function TeamSLADashboard() {
  const [weeksWanted, setWeeksWanted] = useState(4);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/worker-sla", {
        params: { weeks: weeksWanted },
      });
      setData(data);
    } catch (err) {
      logger.error("Worker SLA fetch failed:", err);
      toast.error("Could not load team SLA report");
    } finally {
      setLoading(false);
    }
  }, [weeksWanted]);

  useEffect(() => {
    load();
  }, [load]);

  const onExportCsv = useCallback(async () => {
    try {
      await downloadCsv(
        "/reports/worker-sla.csv",
        { weeks: weeksWanted },
        `team_sla_${weeksWanted}w.csv`,
      );
      toast.success("Team SLA CSV downloaded");
    } catch (err) {
      logger.error("CSV download failed", err);
      toast.error("Could not download CSV");
    }
  }, [weeksWanted]);

  const summary = useMemo(() => {
    if (!data) return { active: 0, avg: 0, top: null };
    const active = data.workers.filter((w) => w.compliance_score != null);
    const avg = active.length
      ? Math.round(active.reduce((a, w) => a + w.compliance_score, 0) / active.length)
      : 0;
    return { active: active.length, avg, top: active[0] || null };
  }, [data]);

  return (
    <div className="space-y-5" data-testid="team-sla-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3 border border-gray-300 bg-white p-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gray-500">
            Compliance · Team SLA Leaderboard
          </div>
          <h2 className="text-2xl font-black tracking-tighter">
            Team performance over the last {weeksWanted} weeks
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Ranked by compliance score (photos attached, geofence behaviour and activity).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex border border-gray-300" role="group">
            {WEEK_OPTIONS.map((n) => (
              <button
                key={n}
                onClick={() => setWeeksWanted(n)}
                data-testid={`teamsla-weeks-${n}`}
                className={`px-3 py-1.5 text-xs font-semibold ${
                  weeksWanted === n
                    ? "bg-[#002FA7] text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                {n}w
              </button>
            ))}
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="teamsla-refresh"
            className="px-3 py-1.5 border border-gray-300 text-xs font-semibold flex items-center gap-1.5 hover:bg-gray-100 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </button>
          <button
            onClick={onExportCsv}
            data-testid="teamsla-export-csv"
            className="px-3 py-1.5 border border-gray-800 bg-gray-900 text-white text-xs font-semibold flex items-center gap-1.5 hover:bg-black"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-gray-200 border border-gray-300">
        <SummaryTile label="Active members" value={summary.active} testid="teamsla-summary-active" />
        <SummaryTile label="Average score" value={summary.avg} suffix="/100" testid="teamsla-summary-avg" />
        <SummaryTile
          label="Top performer"
          value={summary.top ? summary.top.worker_name : "—"}
          accent="#15803d"
          testid="teamsla-summary-top"
        />
      </div>

      <div className="border border-gray-300 bg-white overflow-x-auto">
        {loading && !data && (
          <div className="p-8 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading team SLA…
          </div>
        )}
        {data && data.workers.length === 0 && !loading && (
          <div className="p-8 text-center text-sm text-gray-500" data-testid="teamsla-empty">
            No team members yet.
          </div>
        )}
        {data && data.workers.length > 0 && (
          <table className="min-w-full text-xs" data-testid="teamsla-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-300 text-left">
                <th className="px-3 py-2 font-semibold w-10">#</th>
                <th className="px-3 py-2 font-semibold">Team member</th>
                <th className="px-3 py-2 font-semibold text-center">Score</th>
                <th className="px-3 py-2 font-semibold text-right">Sign-ins</th>
                <th className="px-3 py-2 font-semibold text-right">Hours</th>
                <th className="px-3 py-2 font-semibold text-right">Completions</th>
                <th className="px-3 py-2 font-semibold text-right">Photos</th>
                <th className="px-3 py-2 font-semibold text-right">Sites</th>
                <th className="px-3 py-2 font-semibold text-right">Overrides ✓</th>
                <th className="px-3 py-2 font-semibold text-right">Geofence ✕</th>
              </tr>
            </thead>
            <tbody>
              {data.workers.map((w, i) => {
                const c = scoreColor(w.compliance_score);
                return (
                  <tr
                    key={w.worker_id}
                    className="border-b border-gray-100"
                    data-testid={`teamsla-row-${w.worker_id}`}
                  >
                    <td className="px-3 py-2 font-mono text-gray-500">
                      {i === 0 ? <Trophy className="w-4 h-4 text-amber-500 inline" /> : i + 1}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block w-3 h-3 border border-gray-400"
                          style={{ backgroundColor: w.worker_color?.bg || "#E5E7EB" }}
                        />
                        <div>
                          <div className="font-semibold">{w.worker_name}</div>
                          <div className="text-[10px] text-gray-500">{w.worker_email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span
                        className="inline-block px-2 py-1 font-mono font-bold border min-w-[44px]"
                        style={{ backgroundColor: c.bg, color: c.fg, borderColor: c.border }}
                      >
                        {w.compliance_score == null ? "—" : w.compliance_score}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{w.sign_ins}</td>
                    <td className="px-3 py-2 text-right font-mono">{w.hours}</td>
                    <td className="px-3 py-2 text-right font-mono">{w.completions}</td>
                    <td className="px-3 py-2 text-right font-mono">
                      {w.photos}
                      <span className="text-[10px] text-gray-500 ml-1">
                        ({Math.round(w.photo_rate * 100)}%)
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{w.sites_worked}</td>
                    <td className="px-3 py-2 text-right font-mono">{w.overrides_approved}</td>
                    <td className="px-3 py-2 text-right font-mono">
                      {w.left_without_return > 0 ? (
                        <span className="text-red-700 font-bold">{w.left_without_return}</span>
                      ) : (
                        w.left_geofence
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-600">
        <span className="inline-flex items-center gap-1">
          <Award className="w-3.5 h-3.5" /> Compliance = photo attach rate (60) + geofence
          discipline (30) + activity bonus (10)
        </span>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, suffix, accent, testid }) {
  return (
    <div className="bg-white p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div
        className="text-2xl font-black tracking-tighter mt-1 truncate"
        style={accent ? { color: accent } : undefined}
      >
        {value}
        {suffix && <span className="text-sm text-gray-400 font-medium ml-1">{suffix}</span>}
      </div>
    </div>
  );
}
